/**
 * v3.4.3 — <SingleImportShellIndicator>
 * =============================================================================
 * Companion to <BulkImportShellIndicator>. Renders a subtle pill in the
 * top-right of the app shell whenever the user has an unfinished single
 * statement import waiting in preview. Clicking jumps back to
 * /import/statements to resume categorising.
 *
 * Diya's ask: "I categorised rows, left the page, forgot I was mid-flow.
 * Show me a reminder." This closes that loop end-to-end — the state was
 * already autosaved in v3.4.2; now she gets a nudge to return.
 *
 * Renders nothing when:
 *   • there is no resumable import (hasResumableImport === false), OR
 *   • the user is already on any /import* route (no point nudging them
 *     towards a page they're already on).
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FileClock } from 'lucide-react';
import { useSingleImport } from '@/context/SingleImportContext';

// v3.4.4 — a resumable import older than this auto-clears itself so a
// week-old abandoned session doesn't linger in the shell of a returning
// user. Chosen to match "one working day" — long enough to survive a
// forgotten-overnight, short enough that stale rows don't feel haunting.
const EXPIRY_MS = 24 * 60 * 60 * 1000;

/** "just now" | "12m ago" | "2h ago" | "3d ago" */
function formatRelative(ms) {
  if (!ms) return '';
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function SingleImportShellIndicator() {
  const { hasResumableImport, lastEditedAt, previewRows, resetSingleImport } = useSingleImport();
  const location = useLocation();

  // Tick every 30s so "just now" → "1m ago" → "2m ago" renders live
  // without needing a full page interaction to refresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!hasResumableImport) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [hasResumableImport]);

  // Auto-expire stale imports. Runs in an effect (not during render) so
  // we don't break React's "no side effects during render" rule.
  const isExpired = !!(lastEditedAt && (Date.now() - lastEditedAt > EXPIRY_MS));
  useEffect(() => {
    if (isExpired && hasResumableImport) {
      resetSingleImport();
    }
  }, [isExpired, hasResumableImport, resetSingleImport]);

  // Suppress on any /import route — Diya's already there.
  if (!hasResumableImport) return null;
  if (isExpired) return null;
  if (location.pathname.startsWith('/import')) return null;

  const rowCount = Array.isArray(previewRows) ? previewRows.length : 0;
  const ago = formatRelative(lastEditedAt);
  const title = ago
    ? `Unfinished import from ${ago} — ${rowCount} row${rowCount === 1 ? '' : 's'} waiting. Click to resume.`
    : `${rowCount} row${rowCount === 1 ? '' : 's'} waiting. Click to resume.`;

  return (
    <Link
      to="/import/statements"
      data-testid="single-import-shell-indicator"
      className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/8 text-foreground hover:bg-[hsl(var(--gold))]/14 transition-colors"
      title={title}
    >
      <FileClock className="h-3 w-3 text-[hsl(var(--gold))]" />
      <span>Resume import{ago ? ` · ${ago}` : ''}</span>
    </Link>
  );
}
